import { html } from '/vendor/htm-preact.module.js';
import { Icon } from '/icons.js';

/**
 * What somebody holds, drawn beside their name — GitHub issue #59.
 *
 * WHY A GLYPH AND NOT A WORD. These sit in lists of people, next to a name,
 * on a phone. "Venue manager" beside every name is a column of prose that
 * pushes the names themselves off the row; a mark is read at a glance and
 * costs one character of width. The word is still there for anybody who
 * wants it — as the `title` and the accessible label — so the meaning is
 * never only in a shape.
 *
 * FOUR ROLES, FOUR DISTINCT SHAPES, and they are deliberately not four
 * colours of the same shape. A colour-only difference is invisible to a
 * common form of colour blindness and to anybody in a dim cellar, which is
 * where this app is used. Colour reinforces the shape here; it never carries
 * the meaning alone.
 *
 * The vocabulary is the server's (`api.role_marks`), so a screen cannot
 * invent a role or disagree with another screen about who holds one.
 */
export const ROLE_MARKS = {
  // The whole instance. `shield` because it is authority OVER the place
  // rather than a job inside it, and brass because this app already uses
  // brass for "the thing that matters most on this row".
  admin: { icon: 'shield', label: 'Site admin', tone: 'brassc' },
  // Starts and runs leagues. A trophy is what a league produces.
  // NOT 'League admin'. It is global `league.create` -- permission to
  // START a league -- and reading it as authority over leagues is what
  // left somebody carrying it unable to invite a player into one, or
  // see its pubs. Running a league is the Committee bundle, held at
  // that league.
  league_founder: { icon: 'trophy', label: 'Starts leagues',
                    tone: 'brassc' },
  // Runs a pub. The map pin is already this app's mark for a venue, on the
  // Venues tab and on every venue row, so it needs no learning.
  venue_manager: { icon: 'map-pin', label: 'Venue manager', tone: 'chalkc' },
  // Picks who plays. `users` is already the mark for a circle and its
  // people, which is exactly what this role acts on.
  roster_manager: { icon: 'users', label: 'Roster manager', tone: 'chalkc' },
};

/**
 * The marks for one person.
 *
 * `only` caps how many are drawn, because a name in a narrow row can carry
 * one and a row on the admin panel can carry all of them. The server orders
 * them strongest first, so taking the first N takes the ones that matter.
 */
export function RoleMarks({ roles, only = 0, size = 13 }) {
  const shown = (roles || [])
    .map((r) => [r, ROLE_MARKS[r]])
    // An unknown role from a newer server draws nothing rather than an
    // empty box: this file ships from disk and the API does not, so the two
    // are routinely a deploy apart (see api.py's own note on skew).
    .filter(([, mark]) => Boolean(mark));
  const take = only > 0 ? shown.slice(0, only) : shown;
  if (take.length === 0) return null;

  return html`
    <span class="rolemarks" data-testid="role-marks">
      ${take.map(([id, mark]) => html`
        <span key=${id} class=${mark.tone} title=${mark.label}
              aria-label=${mark.label} role="img"
              data-testid=${`role-mark-${id}`}>
          <${Icon} name=${mark.icon} size=${size} />
        </span>`)}
    </span>`;
}
